import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { CreditCard, Search, RefreshCw, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate, formatAmount } from './adminUtils';

export const PaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchPayments = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20', ...(search && { search }), ...(statusFilter !== 'all' && { status_filter: statusFilter }) });
      const res = await fetch(`${API_BASE}/api/admin/payments?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPayments(d.payments); setTotalPages(d.pages); setTotal(d.total); }
    } catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const exportCSV = async () => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/export/payments`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        const blob = new Blob([d.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = d.filename; a.click();
        toast.success('Downloaded');
      }
    } catch { toast.error('Export failed'); }
  };

  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />Payments
            <Badge className="bg-purple-500/20 text-purple-300">{total}</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white/10 border-white/20 text-white w-40" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm">
              <option value="all" className="bg-slate-800">All</option>
              <option value="completed" className="bg-slate-800">Completed</option>
              <option value="pending" className="bg-slate-800">Pending</option>
            </select>
            <Button variant="outline" size="sm" onClick={exportCSV} className="border-white/20 text-white">
              <Download className="w-4 h-4 mr-1" />CSV
            </Button>
            <Button variant="outline" size="icon" onClick={fetchPayments} className="border-white/20 text-white"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div> : (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10"><TableHead className="text-slate-300">User</TableHead><TableHead className="text-slate-300">Course</TableHead><TableHead className="text-slate-300">Amount</TableHead><TableHead className="text-slate-300">Status</TableHead><TableHead className="text-slate-300">Date</TableHead></TableRow></TableHeader>
              <TableBody>
                {payments.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No payments</TableCell></TableRow> :
                  payments.map(p => (
                    <TableRow key={p.id} className="border-white/10 hover:bg-white/5">
                      <TableCell className="text-white" data-testid={`payment-user-${p.id}`}>
                        {p.user_email && p.user_email !== 'Unknown' ? p.user_email : (
                          <span className="text-slate-400 italic">deleted user <span className="text-xs">({p.user_id?.slice(0, 8)}…)</span></span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-300" data-testid={`payment-course-${p.id}`}>
                        {p.course_type === 'project' ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-indigo-500/20 text-indigo-300 text-[10px] shrink-0">PROJECT</Badge>
                            <span className="truncate max-w-[260px]" title={p.course_display}>{p.course_display}</span>
                          </div>
                        ) : (p.course_display || p.course_id)}
                      </TableCell>
                      <TableCell className="text-emerald-400 font-semibold">{formatAmount((p.currency === 'USD' ? (p.amount / 100) * 90 : p.amount / 100), 'INR')}{p.currency === 'USD' && <span className="ml-1 text-xs text-slate-400">(${(p.amount / 100).toFixed(2)})</span>}</TableCell>
                      <TableCell><Badge className={p.status === 'completed' ? "bg-emerald-500/20 text-emerald-300" : "bg-yellow-500/20 text-yellow-300"}>{p.status}</Badge></TableCell>
                      <TableCell className="text-slate-400 text-sm">{formatDate(p.created_at)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <p className="text-sm text-slate-400" data-testid="payments-page-info">Page {page} of {totalPages} <span className="text-slate-500">· {total} total</span></p>
            <div className="flex flex-wrap items-center gap-1">
              <Button data-testid="payments-prev-page" variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-white/20 text-white"><ChevronLeft className="w-4 h-4" /></Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <Button
                  key={n}
                  data-testid={`payments-page-${n}`}
                  variant={n === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(n)}
                  className={n === page ? 'bg-purple-500 hover:bg-purple-600 text-white min-w-[2.25rem]' : 'border-white/20 text-white min-w-[2.25rem]'}
                >
                  {n}
                </Button>
              ))}
              <Button data-testid="payments-next-page" variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-white/20 text-white"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
